import { AdapterIOStatus } from '@navio/nsdk';
import { ADAPTER_IO_STATUS_LABEL_BY_VALUE } from './labels';

/**
 * Rollup of I/O steps (API: failed | success | pending | partial).
 * `partial` is overall-only — not a step status.
 */
type AdapterIOOverallStatus = AdapterIOStatus | 'partial';

export const ADAPTER_IO_OVERALL_STATUS_LABEL_BY_VALUE: Record<AdapterIOOverallStatus, string> = {
  ...ADAPTER_IO_STATUS_LABEL_BY_VALUE,
  partial: 'Partial'
};

/** Lower = worse for ops (failed first when choosing which detail to open). */
const ADAPTER_IO_OVERALL_STATUS_RANK: Record<AdapterIOOverallStatus, number> = {
  failed: 0,
  retrying: 1,
  pending: 2,
  partial: 3,
  success: 4,
  skipped: 5
};

function isAdapterIOOverallStatus(status: string): status is AdapterIOOverallStatus {
  return status in ADAPTER_IO_OVERALL_STATUS_LABEL_BY_VALUE;
}

export function adapterIOOverallStatusLabel(status: string | null | undefined): string | null {
  if (!status) {
    return null;
  }
  return isAdapterIOOverallStatus(status) ? ADAPTER_IO_OVERALL_STATUS_LABEL_BY_VALUE[status] : status;
}

/** Prefer the worse of two overall statuses (failed > retrying > … > skipped). */
export function worseAdapterIOOverallStatus(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a) {
    return b ?? null;
  }
  if (!b) {
    return a;
  }
  const ra = isAdapterIOOverallStatus(a) ? ADAPTER_IO_OVERALL_STATUS_RANK[a] : 99;
  const rb = isAdapterIOOverallStatus(b) ? ADAPTER_IO_OVERALL_STATUS_RANK[b] : 99;
  return ra <= rb ? a : b;
}

/**
 * Prefer the detail id whose overall status is worse (so failed work surfaces).
 * When only one id exists, return that id. Equal ranks prefer push.
 */
export function preferredAdapterIODetailId(args: {
  pushDetailId?: number | null;
  pullDetailId?: number | null;
  pushOverall?: string | null;
  pullOverall?: string | null;
}): number | null {
  const { pushDetailId, pullDetailId, pushOverall, pullOverall } = args;
  if (pushDetailId != null && pullDetailId != null) {
    const pushRank =
      pushOverall && isAdapterIOOverallStatus(pushOverall) ? ADAPTER_IO_OVERALL_STATUS_RANK[pushOverall] : 99;
    const pullRank =
      pullOverall && isAdapterIOOverallStatus(pullOverall) ? ADAPTER_IO_OVERALL_STATUS_RANK[pullOverall] : 99;
    return pushRank <= pullRank ? pushDetailId : pullDetailId;
  }
  return pushDetailId ?? pullDetailId ?? null;
}
